/**
 * Authentication Anomaly Detection Service
 *
 * Integrates with database-level anomaly detection to:
 * - Record authentication patterns on each login
 * - Detect suspicious activity (new devices, locations, times)
 * - Alert users of security concerns
 * - Build trust scores over time
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { prodLogger } from '@/utils/productionLogger';

export interface AuthAnomaly {
  anomaly_type: 'new_device' | 'new_location' | 'unusual_time' | 'rapid_attempts' | 'geographic_impossibility' | 'suspicious_user_agent' | 'multiple_sessions';
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  metadata: Record<string, any>;
}

export interface GeoLocation {
  country_code?: string;
  city?: string;
}

// Cache geolocation for 1 hour to prevent rate limiting
let geoCache: { data: GeoLocation; timestamp: number } | null = null;
const GEO_CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Get approximate geolocation from IP address
 * Uses public IP geolocation API (no API key required for basic info)
 * Caches result for 1 hour to avoid rate limiting
 */
async function getGeolocation(): Promise<GeoLocation> {
  // Return cached data if still valid
  if (geoCache && Date.now() - geoCache.timestamp < GEO_CACHE_DURATION) {
    return geoCache.data;
  }

  try {
    // Use api.ipify.org for IP detection (no CORS issues, no rate limits)
    // Note: ipapi.co blocks CORS requests from browsers
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch IP: ${response.status}`);
    }

    const data = await response.json();

    // Basic geolocation data - we can only get IP from this service
    // For privacy and CORS reasons, we'll skip detailed location data
    const geoData: GeoLocation = {
      country_code: undefined, // Not available from ipify
      city: undefined, // Not available from ipify
    };

    // Update cache
    geoCache = {
      data: geoData,
      timestamp: Date.now(),
    };

    return geoData;
  } catch (error) {
    logger.warn('Failed to get geolocation:', error);
    // Return cached data if available, otherwise empty object
    return geoCache?.data || {};
  }
}

/**
 * Get user's IP address
 * Uses public IP detection service
 */
async function getIPAddress(): Promise<string | null> {
  try {
    // Use api.ipify.org (no CORS issues, no rate limits, simple and reliable)
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });

    if (!response.ok) {
      throw new Error('Failed to fetch IP address from ipify');
    }

    const data = await response.json();
    return data.ip || null;
  } catch (error) {
    logger.warn('Failed to get IP address:', error);
    return null;
  }
}

/**
 * Record authentication attempt in database
 * Builds user behavior patterns over time
 */
export async function recordAuthAttempt(userId: string): Promise<void> {
  try {
    const userAgent = navigator.userAgent;
    const ipAddress = await getIPAddress();
    const geolocation = await getGeolocation();

    if (!ipAddress) {
      logger.warn('Cannot record auth attempt without IP address');
      return;
    }

    // Call database function to record attempt
    const { error } = await supabase.rpc('record_auth_attempt', {
      _user_id: userId,
      _user_agent: userAgent,
      _ip_address: ipAddress,
      _country_code: geolocation.country_code || null,
      _city: geolocation.city || null,
    });

    if (error) {
      logger.error('Failed to record auth attempt:', error);
    } else {
      logger.debug('Auth attempt recorded for user:', userId);
    }
  } catch (error) {
    logger.error('Error recording auth attempt:', error);
  }
}

/**
 * Detect authentication anomalies
 * Returns list of detected anomalies (empty if none found)
 */
export async function detectAuthAnomalies(userId: string): Promise<AuthAnomaly[]> {
  try {
    const userAgent = navigator.userAgent;
    const ipAddress = await getIPAddress();
    const geolocation = await getGeolocation();

    if (!ipAddress) {
      logger.warn('Cannot detect anomalies without IP address');
      return [];
    }

    // Call database function to detect anomalies
    const { data, error } = await supabase.rpc('detect_auth_anomalies', {
      _user_id: userId,
      _user_agent: userAgent,
      _ip_address: ipAddress,
      _country_code: geolocation.country_code || null,
    });

    if (error) {
      logger.error('Failed to detect auth anomalies:', error);
      return [];
    }

    // Log detected anomalies
    if (data && data.length > 0) {
      prodLogger.warn('Auth anomalies detected:', {
        userId,
        anomalyCount: data.length,
        anomalies: data,
      });

      // Store anomalies in database for review
      for (const anomaly of data) {
        await storeAnomaly(userId, anomaly, ipAddress, userAgent, geolocation.country_code);
      }
    }

    return data || [];
  } catch (error) {
    logger.error('Error detecting auth anomalies:', error);
    return [];
  }
}

/**
 * Store detected anomaly in database for security review
 */
async function storeAnomaly(
  userId: string,
  anomaly: AuthAnomaly,
  ipAddress: string,
  userAgent: string,
  countryCode?: string
): Promise<void> {
  try {
    const { error } = await supabase.from('auth_anomalies').insert({
      user_id: userId,
      anomaly_type: anomaly.anomaly_type,
      risk_level: anomaly.risk_level,
      description: anomaly.description,
      metadata: anomaly.metadata,
      ip_address: ipAddress,
      user_agent: userAgent,
      country_code: countryCode,
      status: 'pending',
    });

    if (error) {
      logger.error('Failed to store anomaly:', error);
    }
  } catch (error) {
    logger.error('Error storing anomaly:', error);
  }
}

/**
 * Get user-friendly message for anomaly
 */
export function getAnomalyMessage(anomaly: AuthAnomaly): string {
  switch (anomaly.anomaly_type) {
    case 'new_device':
      return 'We noticed you logged in from a new device. If this was you, you can ignore this message.';

    case 'new_location':
      return `We detected a login from a new location (${anomaly.metadata.country_code}). If this wasn't you, please secure your account immediately.`;

    case 'unusual_time':
      return 'You logged in at an unusual time. If this was you, you can ignore this message.';

    case 'rapid_attempts':
      return '⚠️ SECURITY ALERT: Multiple login attempts detected. If you didn\'t make these attempts, your account may be under attack. Please change your password immediately.';

    case 'geographic_impossibility':
      return `⚠️ CRITICAL SECURITY ALERT: Impossible travel detected (${anomaly.metadata.from_country} to ${anomaly.metadata.to_country} in ${Math.round(anomaly.metadata.time_diff_minutes)} minutes). Please secure your account immediately.`;

    case 'suspicious_user_agent':
      return 'We detected a login from an unusual browser or device. If this wasn\'t you, please secure your account.';

    case 'multiple_sessions':
      return 'Multiple active sessions detected. If you\'re not using multiple devices, please review your active sessions.';

    default:
      return 'Unusual activity detected on your account. Please review your recent logins.';
  }
}

/**
 * Get security recommendations based on risk level
 */
export function getSecurityRecommendations(riskLevel: string): string[] {
  switch (riskLevel) {
    case 'critical':
      return [
        'Change your password immediately',
        'Review recent account activity',
        'Enable two-factor authentication',
        'Sign out all other sessions',
        'Contact support if you did not initiate this login',
      ];

    case 'high':
      return [
        'Verify this login was made by you',
        'Consider changing your password',
        'Enable two-factor authentication if not already enabled',
        'Review active sessions',
      ];

    case 'medium':
      return [
        'Verify this login was made by you',
        'Review recent account activity',
        'Consider enabling two-factor authentication',
      ];

    case 'low':
      return [
        'This is likely normal activity',
        'No immediate action required',
        'Keep your password secure',
      ];

    default:
      return ['Review your account security settings'];
  }
}

/**
 * Check if user should be alerted about anomalies
 * Only alert on medium+ risk levels
 */
export function shouldAlertUser(anomalies: AuthAnomaly[]): boolean {
  return anomalies.some(a => a.risk_level === 'medium' || a.risk_level === 'high' || a.risk_level === 'critical');
}

/**
 * Get highest risk level from anomalies
 */
export function getHighestRiskLevel(anomalies: AuthAnomaly[]): string {
  if (anomalies.some(a => a.risk_level === 'critical')) return 'critical';
  if (anomalies.some(a => a.risk_level === 'high')) return 'high';
  if (anomalies.some(a => a.risk_level === 'medium')) return 'medium';
  if (anomalies.some(a => a.risk_level === 'low')) return 'low';
  return 'none';
}

